import React, { useState, useEffect } from "react";
import { BiCheckbox } from "react-icons/bi";
import { FaBars, FaTimes } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

function AboutDla() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [aboutContent, setAboutContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Direct API fetch without local storage
  useEffect(() => {
    const fetchAboutContent = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `https://backend.dlapakistan.com/api/about/about-dla/active/?_=${Date.now()}`
        );
        setAboutContent(response.data);
      } catch (error) {
        console.error("Error fetching about content:", error);
        // Fallback content
        setAboutContent({
          title: "About The DLA",
          subtitle: "The Dental Laboratories Association Ltd (DLA) is a professional body for dental laboratory owners in the Pakistan.",
          description: `
            <p>It is estimated that members of the DLA are responsible for over 80% of the dental laboratory services in the Pakistan. To represent effectively the interests of the members and provide a range of professional services to support and develop their businesses.</p>
            
            <div class="mt-6"> 
              <h3 class="text-black font-bold mb-4 text-xl">The DLA Aims To:</h3>
              <ul class="space-y-4">
                <li class="flex items-start">
                  <span class="mr-3 mt-1 text-[#70B444]">•</span>
                  <span>Inform and advise members of the latest industry developments.</span>
                </li>
                <li class="flex items-start">
                  <span class="mr-3 mt-1 text-[#70B444]">•</span>
                  <span>Support and implement initiatives to improve standards.</span>
                </li>
                <li class="flex items-start">
                  <span class="mr-3 mt-1 text-[#70B444]">•</span>
                  <span>Encourage schemes which help improve technical knowledge.</span>
                </li>
                <li class="flex items-start">
                  <span class="mr-3 mt-1 text-[#70B444]">•</span>
                  <span>Lobby relevant government bodies and dental organisations.</span>
                </li>
                <li class="flex items-start">
                  <span class="mr-3 mt-1 text-[#70B444]">•</span>
                  <span>Promote the membership to the dental profession.</span>
                </li>
              </ul>
            </div>
          `
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAboutContent();
  }, []);

  const sidebarLinks = [
    { text: "About Us", border: "#ccc", showIcon: false, path: "/about" },
    { text: "Meet the Board", border: "#ccc", showIcon: true, path: "/about/meet-the-board" },
    { text: "Work We Do", border: null, showIcon: true, path: "/about/work-we-do" },
    { text: "Education", border: null, showIcon: false, path: "/about/work-we-do/education" },
    { text: "Regulation", border: null, showIcon: false, path: "/about/work-we-do/regulation" },
    { text: "Membership Support", border: "#ccc", showIcon: false, path: "/about/work-we-do/membership-support" },
    { text: "Our Policies", border: "#ccc", showIcon: true, path: "/about/our-policies" },
    { text: "Become a Member", border: "#ccc", showIcon: true, path: "/about/become-member" },
    { text: "DLA Fellowship", border: null, showIcon: true, path: "/about/dla-fellowship" },
    { text: "DLA Fellowship Awards", border: null, showIcon: false, path: "/about/dla-fellowship/awards" },
    { text: "DLA Special Recognition Award", border: "#ccc", showIcon: false, path: "/about/dla-fellowship/special-recognition" },
  ];

  const handleSidebarClick = (path) => {
    navigate(path);
    if (windowWidth <= 1029) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const isMobileView = windowWidth <= 1029;

  const getTopSpacing = () => {
    if (windowWidth < 640) return "pt-20";
    if (windowWidth < 768) return "pt-24";
    if (windowWidth < 1024) return "pt-28";
    if (windowWidth < 1175.56) return "pt-32";
    return "pt-40";
  };

  if (loading) {
    return (
      <div className={`w-full ${getTopSpacing()}`}>
        <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${getTopSpacing()}`}>
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        {/* Breadcrumb */}
        <div className="flex flex-wrap items-center mt-10 text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            About The DLA
          </span>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">
          {!isMobileView && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">About The DLA</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item.path)}
                  className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  } ${location.pathname === item.path ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""}`}
                >
                  {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                  <span className="break-words">{item.text}</span>
                </div>
              ))}
            </div>
          )}

          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                {aboutContent?.title || "About The DLA"}
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] px-3 py-2 rounded-md hover:bg-[#5d9a3a] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            <div className="prose prose-lg max-w-none">
              {aboutContent?.subtitle && (
                <p className="text-gray-900 text-base sm:text-lg md:text-xl font-bold leading-relaxed mb-6">
                  {aboutContent.subtitle}
                </p>
              )}

              {aboutContent?.description && (
                <div 
                  className="about-content" 
                  dangerouslySetInnerHTML={{ __html: aboutContent.description }}
                />
              )}
            </div>
          </div>

          {isMobileView && (
            <div
              className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                isSidebarOpen ? "translate-x-0" : "translate-x-full"
              }`}
            >
              <div className="flex flex-col h-full">
                <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                  <span className="font-bold text-lg">Navigation</span>
                  <FaTimes 
                    className="cursor-pointer text-2xl" 
                    onClick={toggleSidebar} 
                  />
                </div>
                <div className="overflow-y-auto flex-1 p-2">
                  {sidebarLinks.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSidebarClick(item.path)}
                      className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center hover:bg-gray-50 ${
                        location.pathname === item.path 
                          ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                          : "text-gray-700"
                      }`}
                    >
                      {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isMobileView && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              onClick={toggleSidebar}
            />
          )}
        </div>
      </div>

      <style jsx>{`
        .about-content, .about-content * {
          background-color: transparent !important;
        }
        .about-content h1, .about-content h2, .about-content h3 {
          color: #70B444;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        .about-content p {
          margin-bottom: 1.2em;
          line-height: 1.8;
          color: #374151;
        }
        .about-content ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin-bottom: 1.5em;
        }
        .about-content li {
          margin-bottom: 0.8em;
          color: #374151;
        }
        .about-content a {
          color: #70B444;
          text-decoration: underline;
        }
        .about-content strong {
          color: #111827;
        }
      `}</style>
    </div>
  );
}

export default AboutDla;